import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Cloud, 
  Upload, 
  CheckCircle, 
  XCircle, 
  Image, 
  FileText, 
  Trash2, 
  RefreshCw,
  AlertTriangle,
  Mail,
  Send,
  Server,
  Download,
  Database,
  HardDrive,
  Clock,
  X,
  ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MigrationPage = () => {
  const navigate = useNavigate();
  const [scanResults, setScanResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  const [migrationResults, setMigrationResults] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [cloudinaryStatus, setCloudinaryStatus] = useState(null);
  const [options, setOptions] = useState({
    deleteLocal: false,
    migrateProducts: true,
    migrateLogos: true,
    migrateWatermarks: true,
  });
  
  // Backup states
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [backupResult, setBackupResult] = useState(null);
  const [showBackupPopup, setShowBackupPopup] = useState(false);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);

  const token = localStorage.getItem('ebb_token');

  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : null,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  };

  // Load initial statuses
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const [cloudinary, email, backup] = await Promise.all([
          fetch(`${BACKEND_URL}/api/cloudinary/status`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/email/status`).then(r => r.json()),
          apiCall('/api/backup/status').catch(() => ({})),
        ]);
        setCloudinaryStatus(cloudinary);
        setEmailStatus(email);
        setBackupStatus(backup);
        setAutoBackupEnabled(backup?.auto_backup_enabled || false);
      } catch (e) {
        console.error('Error loading statuses:', e);
      }
    };
    loadStatuses();
  }, []);

  // Create backup
  const handleCreateBackup = async () => {
    setBackupInProgress(true);
    setShowBackupPopup(true);
    setBackupResult(null);
    
    try {
      const result = await apiCall('/api/backup/create', 'POST');
      setBackupResult(result);
    } catch (error) {
      setBackupResult({ success: false, error: error.message });
    } finally {
      setBackupInProgress(false);
    }
  };

  // Toggle auto backup
  const handleToggleAutoBackup = async () => {
    try {
      const newValue = !autoBackupEnabled;
      await apiCall(`/api/backup/auto-settings?enabled=${newValue}`, 'POST');
      setAutoBackupEnabled(newValue);
    } catch (error) {
      alert(error.message);
    }
  };

  // Scan local files
  const handleScan = async () => {
    setLoading(true);
    try {
      const results = await apiCall('/api/migration/scan');
      setScanResults(results);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Migrate products
  const handleMigrateProducts = async () => {
    setMigrating(true);
    setProgress({ current: 0, total: 1, status: 'Migration des images produits...' });
    try {
      const results = await apiCall(
        `/api/migration/migrate-products?delete_local=${options.deleteLocal}`,
        'POST'
      );
      setMigrationResults(prev => ({ ...prev, products: results }));
      setProgress({ current: 1, total: 1, status: 'Terminé !' });
    } catch (error) {
      alert(error.message);
    } finally {
      setMigrating(false);
    }
  };

  // Migrate logos and watermarks
  const handleMigrateLogos = async () => {
    setMigrating(true);
    setProgress({ current: 0, total: 1, status: 'Migration des logos et watermarks...' });
    try {
      const results = await apiCall(
        `/api/migration/migrate-logos?delete_local=${options.deleteLocal}`,
        'POST'
      );
      setMigrationResults(prev => ({ ...prev, logos: results }));
      setProgress({ current: 1, total: 1, status: 'Terminé !' });
    } catch (error) {
      alert(error.message);
    } finally {
      setMigrating(false);
    }
  };

  // Run full migration
  const handleFullMigration = async () => {
    setMigrating(true);
    setMigrationResults(null);
    const steps = [];
    if (options.migrateProducts) steps.push('products');
    if (options.migrateLogos || options.migrateWatermarks) steps.push('logos');
    
    try {
      for (let i = 0; i < steps.length; i++) {
        setProgress({ current: i, total: steps.length, status: `Étape ${i + 1}/${steps.length}: ${steps[i]}...` });
        
        if (steps[i] === 'products') {
          const results = await apiCall(
            `/api/migration/migrate-products?delete_local=${options.deleteLocal}`,
            'POST'
          );
          setMigrationResults(prev => ({ ...prev, products: results }));
        } else if (steps[i] === 'logos') {
          const results = await apiCall(
            `/api/migration/migrate-logos?delete_local=${options.deleteLocal}`,
            'POST'
          );
          setMigrationResults(prev => ({ ...prev, logos: results }));
        }
      }
      setProgress({ current: steps.length, total: steps.length, status: 'Migration terminée !' });
    } catch (error) {
      alert(error.message);
    } finally {
      setMigrating(false);
    }
  };

  // Send test email
  const handleTestEmail = async () => {
    if (!testEmail) {
      alert('Veuillez entrer une adresse email');
      return;
    }
    setSendingTest(true);
    try {
      await apiCall(`/api/email/test?to_email=${encodeURIComponent(testEmail)}`, 'POST');
      alert(`Email de test envoyé à ${testEmail}`);
    } catch (error) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Server className="w-8 h-8 text-amber-600" />
              Configuration & Migration
            </h1>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour Admin
            </button>
          </div>
          <p className="text-gray-500 mt-1">Gérez le stockage cloud et les services email</p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cloudinary Status */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Cloud className="w-5 h-5 text-blue-500" />
                Cloudinary (Stockage Cloud)
              </h2>
              {cloudinaryStatus?.configured ? (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Configuré
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-600 text-sm">
                  <XCircle className="w-4 h-4" />
                  Non configuré
                </span>
              )}
            </div>
            {cloudinaryStatus?.configured && (
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p><strong>Cloud Name:</strong> {cloudinaryStatus.cloud_name}</p>
                <p className="text-blue-600 mt-1">Les nouvelles images seront automatiquement uploadées vers Cloudinary</p>
              </div>
            )}
          </div>

          {/* Email Status */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-500" />
                Email (IONOS SMTP)
              </h2>
              {emailStatus?.configured ? (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Configuré
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-600 text-sm">
                  <XCircle className="w-4 h-4" />
                  Non configuré
                </span>
              )}
            </div>
            {emailStatus?.configured && (
              <div className="space-y-3">
                <div className="bg-purple-50 rounded-lg p-3 text-sm">
                  <p><strong>Serveur:</strong> {emailStatus.smtp_host}:{emailStatus.smtp_port}</p>
                  <p><strong>Expéditeur:</strong> {emailStatus.sender_email}</p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Email de test..."
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  />
                  <button
                    onClick={handleTestEmail}
                    disabled={sendingTest}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Tester
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Migration Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-600" />
            Migration vers Cloudinary
          </h2>

          {/* Scan Button */}
          <div className="mb-6">
            <button
              onClick={handleScan}
              disabled={loading || !cloudinaryStatus?.configured}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
              Scanner les fichiers locaux
            </button>
            {!cloudinaryStatus?.configured && (
              <p className="text-red-500 text-sm mt-2">Cloudinary doit être configuré pour utiliser la migration</p>
            )}
          </div>

          {/* Scan Results */}
          {scanResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <Image className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                  <div className="text-2xl font-bold text-blue-700">{scanResults.products?.length || 0}</div>
                  <div className="text-sm text-blue-600">Images produits</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <FileText className="w-8 h-8 mx-auto text-green-500 mb-2" />
                  <div className="text-2xl font-bold text-green-700">{scanResults.logos?.length || 0}</div>
                  <div className="text-sm text-green-600">Logos</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <Image className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  <div className="text-2xl font-bold text-purple-700">{scanResults.watermarks?.length || 0}</div>
                  <div className="text-sm text-purple-600">Watermarks</div>
                </div>
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <Cloud className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                  <div className="text-2xl font-bold text-gray-700">{scanResults.total_size_human}</div>
                  <div className="text-sm text-gray-600">Taille totale</div>
                </div>
              </div>

              {/* Options */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Options de migration</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.migrateProducts}
                      onChange={(e) => setOptions(prev => ({ ...prev, migrateProducts: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span>Migrer les images produits</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.migrateLogos}
                      onChange={(e) => setOptions(prev => ({ ...prev, migrateLogos: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span>Migrer les logos</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.migrateWatermarks}
                      onChange={(e) => setOptions(prev => ({ ...prev, migrateWatermarks: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span>Migrer les watermarks</span>
                  </label>
                  <label className="flex items-center gap-2 text-orange-600">
                    <input
                      type="checkbox"
                      checked={options.deleteLocal}
                      onChange={(e) => setOptions(prev => ({ ...prev, deleteLocal: e.target.checked }))}
                      className="w-4 h-4 rounded border-orange-300"
                    />
                    <span className="flex items-center gap-1">
                      <Trash2 className="w-4 h-4" />
                      Supprimer les fichiers locaux après migration
                    </span>
                  </label>
                </div>
              </div>

              {/* Migration Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleFullMigration}
                  disabled={migrating}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {migrating ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  Lancer la migration complète
                </button>
                <button
                  onClick={handleMigrateProducts}
                  disabled={migrating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  Migrer produits uniquement
                </button>
                <button
                  onClick={handleMigrateLogos}
                  disabled={migrating}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                >
                  Migrer logos uniquement
                </button>
              </div>

              {/* Progress */}
              {migrating && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="w-5 h-5 text-amber-600 animate-spin" />
                    <span className="font-medium text-amber-800">{progress.status}</span>
                  </div>
                  <div className="w-full bg-amber-200 rounded-full h-2">
                    <div
                      className="bg-amber-600 h-2 rounded-full transition-all"
                      style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Results */}
              {migrationResults && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Résultats de la migration
                  </h3>
                  
                  {migrationResults.products && (
                    <div className="mb-3">
                      <p className="text-sm"><strong>Images produits:</strong></p>
                      <ul className="text-sm text-green-700 ml-4 list-disc">
                        <li>Migrées: {migrationResults.products.migrated}</li>
                        <li>Ignorées (déjà sur Cloudinary): {migrationResults.products.skipped}</li>
                        <li>Échecs: {migrationResults.products.failed}</li>
                        <li>Produits mis à jour: {migrationResults.products.products_updated?.length || 0}</li>
                      </ul>
                      {migrationResults.products.errors?.length > 0 && (
                        <div className="mt-2 text-red-600 text-sm">
                          <p className="font-medium">Erreurs:</p>
                          {migrationResults.products.errors.slice(0, 5).map((err, i) => (
                            <p key={i} className="ml-4">• {err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {migrationResults.logos && (
                    <div>
                      <p className="text-sm"><strong>Logos & Watermarks:</strong></p>
                      <ul className="text-sm text-green-700 ml-4 list-disc">
                        <li>Logos migrés: {migrationResults.logos.logos_migrated}</li>
                        <li>Watermarks migrés: {migrationResults.logos.watermarks_migrated}</li>
                        <li>Échecs: {migrationResults.logos.failed}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Backup Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-green-600" />
            Sauvegarde des données
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Manual Backup */}
            <div className="bg-green-50 rounded-xl p-5">
              <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                Sauvegarde manuelle
              </h3>
              <p className="text-sm text-green-700 mb-4">
                Créez une sauvegarde complète de toutes vos données (produits, clients, commandes, factures, cartes cadeaux).
              </p>
              <button
                onClick={handleCreateBackup}
                disabled={backupInProgress}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {backupInProgress ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Sauvegarde en cours...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Créer une sauvegarde
                  </>
                )}
              </button>
              
              {backupStatus?.last_backup && (
                <p className="mt-3 text-xs text-green-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Dernière sauvegarde: {new Date(backupStatus.last_backup).toLocaleString('fr-BE')}
                </p>
              )}
            </div>

            {/* Auto Backup */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Sauvegarde automatique
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                Activez la sauvegarde automatique quotidienne pour ne jamais perdre vos données.
              </p>
              <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                <div className={`relative w-12 h-6 rounded-full transition-colors ${autoBackupEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${autoBackupEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                </div>
                <input
                  type="checkbox"
                  checked={autoBackupEnabled}
                  onChange={handleToggleAutoBackup}
                  className="hidden"
                />
                <span className={`font-medium ${autoBackupEnabled ? 'text-blue-800' : 'text-gray-600'}`}>
                  {autoBackupEnabled ? 'Activé' : 'Désactivé'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Informations importantes</h3>
              <ul className="mt-2 text-sm text-amber-700 space-y-1">
                <li>• <strong>Cloudinary gratuit:</strong> 25 GB de stockage et 25 GB de bande passante/mois</li>
                <li>• <strong>Images produits:</strong> Stockées dans le dossier <code>products/</code> sur Cloudinary</li>
                <li>• <strong>PDFs factures:</strong> Générés à la demande, peuvent être archivés manuellement</li>
                <li>• <strong>Templates factures:</strong> Stockés en base de données (MongoDB), non concernés par la migration</li>
                <li>• <strong>Suppression locale:</strong> Recommandé uniquement après vérification de la migration</li>
                <li>• <strong>Sauvegarde:</strong> Inclut produits, clients, commandes, factures et cartes cadeaux</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Backup Popup */}
      {showBackupPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className={`p-6 text-center ${backupResult?.success ? 'bg-green-500' : backupInProgress ? 'bg-blue-500' : 'bg-red-500'}`}>
              {backupInProgress ? (
                <>
                  <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white">Sauvegarde en cours...</h3>
                  <p className="text-white/80 mt-2">Veuillez patienter</p>
                </>
              ) : backupResult?.success ? (
                <>
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-white" />
                  <h3 className="text-xl font-bold text-white">Sauvegarde réussie !</h3>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 mx-auto mb-4 text-white" />
                  <h3 className="text-xl font-bold text-white">Erreur</h3>
                </>
              )}
            </div>
            
            {!backupInProgress && backupResult && (
              <div className="p-6">
                {backupResult.success ? (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Fichier:</span>
                        <span className="font-mono text-gray-900">{backupResult.filename}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Taille:</span>
                        <span className="font-medium">{backupResult.size_human}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Collections:</span>
                        <span className="font-medium">{backupResult.collections}</span>
                      </div>
                    </div>
                    
                    <a
                      href={`${BACKEND_URL}${backupResult.download_url}`}
                      download
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Télécharger la sauvegarde
                    </a>
                  </div>
                ) : (
                  <p className="text-red-600 text-center">{backupResult.error}</p>
                )}
                
                <button
                  onClick={() => setShowBackupPopup(false)}
                  className="w-full mt-4 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MigrationPage;
